import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SourceMetaForm, {
  EMPTY_SOURCE_META,
  isSourceMetaComplete,
  type SourceMeta,
} from '../components/upload/SourceMetaForm';
import { useAuth } from '../contexts/AuthContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { createFactCheckItem, updateFactCheckItem } from '../services/factCheckItemService';
import {
  createUploadResumable,
  formatFileSize,
  initializeVideoUpload,
  isValidFileSize,
  isValidVideoFile,
  startVideoUpload,
} from '../services/videoUploadService';
import { IMG } from '../theme/assets';
import type { VideoMode } from '../types';

/** 文字起こしの言語。本番の実績は 54/54 が日本語なので既定は ja。 */
const LANGUAGES = [
  { value: 'ja', label: '日本語' },
  { value: 'en', label: '英語' },
  { value: 'zh', label: '中国語' },
  { value: 'ko', label: '韓国語' },
] as const;

const CreateVideoItem: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const tokens = useThemeColors();

  // アイテム情報
  const [language, setLanguage] = useState('ja');
  const [videoMode, setVideoMode] = useState<VideoMode>('with_telop');
  /** 名称をこちらで入れたか。人が打ち直したら false に戻る */
  const [titleAutoFilled, setTitleAutoFilled] = useState(false);
  /** 言語は既定（日本語）のままで足りるので畳んでおく */
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // 放送録画など CM を含む素材。指定すると文字起こしから CM 区間を推定して解析対象から外す
  const [hasCommercials, setHasCommercials] = useState(false);
  // 資料メタ情報（Firestore に保存し、抽出時にバックエンドが読む）
  const [sourceMeta, setSourceMeta] = useState<SourceMeta>(EMPTY_SOURCE_META);

  // 動画アップロード
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 案件ID（案件からの作成時に使用）
  const workspaceId = (location.state as any)?.workspaceId || null;

  const handleFileSelect = (file: File) => {
    if (!isValidVideoFile(file)) {
      setError('対応している動画形式: MP4, MOV, WebM');
      return;
    }

    if (!isValidFileSize(file)) {
      setError('ファイルサイズは20GB以下にしてください。');
      return;
    }

    setSelectedFile(file);
    setError(null);

    // 名称はファイル名から入れる（そのまま編集できる）。
    // 人が自分で打ったものは上書きしない。入れ直しのときは付け替える。
    if (!sourceMeta.title.trim() || titleAutoFilled) {
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      setSourceMeta((m) => ({ ...m, title: nameWithoutExt }));
      setTitleAutoFilled(true);
    }
  };

  const handleStartUpload = async () => {
    if (!selectedFile || !user) {
      setError('動画ファイルを選択してください。');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      // 1. Firestoreにアイテムを作成 (API経由)
      const item = await createFactCheckItem({
        workspaceId: workspaceId,
        type: 'video_upload',
        // 表示用のタイトル・詳細は資料の名称・概要をそのまま使う（入力を二重にしない）
        title: sourceMeta.title,
        description: sourceMeta.summary,
        fileName: selectedFile.name,
        status: 'extracting',
        sourceMeta,
      });

      console.log('📝 FactCheckItem作成完了:', item.id);

      // 2. バックエンドでResumable Upload URI取得
      const initResponse = await initializeVideoUpload(item.id, selectedFile);

      console.log('📝 アップロードURI取得完了:', initResponse);

      // 3. Firestoreにアップロード情報を保存 (API経由)
      const resumableInfo = createUploadResumable(selectedFile, initResponse.uploadUri, 0);
      await updateFactCheckItem(item.id, {
        blobName: initResponse.blobName,
        mimeType: selectedFile.type,
        sizeBytes: selectedFile.size,
        processingStage: 'uploading',
        transcriptLanguage: language,
        videoMode: videoMode,
        hasCommercials,
        uploadResumable: resumableInfo,
      });

      // 4. アップロード開始（バックグラウンドで継続）
      startVideoUpload(item.id, selectedFile, initResponse.uploadUri, initResponse.blobName);

      // 処理ステータス画面へ遷移
      navigate(`/video/processing/${item.id}`, {
        state: { workspaceId },
      });
    } catch (err) {
      console.error('アップロード初期化エラー:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`初期化エラー: ${errorMessage}`);
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // --- スタイル定義（mock create-video.html 準拠 / トークンでダーク対応） ---
  const panelSx = {
    bgcolor: tokens.paper,
    border: `1px solid ${tokens.line}`,
    borderRadius: '13px',
    p: '20px 22px',
    boxShadow: '0 8px 24px rgba(30,30,70,.06)',
  } as const;

  const selectSx = {
    borderRadius: '11px',
    bgcolor: tokens.paper,
    fontSize: 14,
    color: tokens.ink,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line, borderWidth: '1.5px' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.teal,
      borderWidth: '1.5px',
    },
    '& .MuiSelect-icon': { color: tokens.muted },
  } as const;

  const FieldLabel = ({
    children,
    required,
    optional,
  }: {
    children: React.ReactNode;
    required?: boolean;
    optional?: boolean;
  }) => (
    <Typography
      component="label"
      sx={{ display: 'block', fontSize: 12, fontWeight: 800, color: tokens.muted, mb: 0.9 }}
    >
      {children}
      {required && (
        <Box component="span" sx={{ color: '#dc2626', ml: 0.5 }}>
          *
        </Box>
      )}
      {optional && (
        <Box component="span" sx={{ color: tokens.faint, fontWeight: 700, ml: 0.6 }}>
          任意
        </Box>
      )}
    </Typography>
  );

  const hintSx = { fontSize: 11, color: tokens.faint, mt: 0.8 } as const;
  const stepBadgeSx = {
    width: 22,
    height: 22,
    borderRadius: '50%',
    bgcolor: tokens.teal,
    color: '#fff',
    fontSize: 12,
    fontWeight: 900,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  } as const;

  return (
    <Box sx={{ maxWidth: 1040, mx: 'auto', py: 4, px: { xs: 2, md: 0 } }}>
      {/* ヘッダー。左メニューがあるので「戻る」は置かない */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.2 }}>
        <Typography sx={{ fontSize: 21, fontWeight: 900, color: tokens.ink }}>
          新規動画を読み込ませる
        </Typography>
      </Box>

      {/* ヒーロー */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          background: `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
          border: `1px solid ${tokens.line}`,
          borderRadius: '13px',
          p: '16px 22px',
          mb: 2.2,
          boxShadow: '0 8px 24px rgba(30,30,70,.06)',
        }}
      >
        <Box
          component="img"
          src={IMG.owl.clipboard}
          alt=""
          sx={{
            width: 62,
            height: 62,
            objectFit: 'contain',
            flexShrink: 0,
            filter: 'drop-shadow(0 6px 12px rgba(30,30,70,.16))',
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink }}>
            検証する動画を取り込みましょう。
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: tokens.muted, mt: 0.5, lineHeight: 1.5 }}>
            音声を文字起こしし、その内容からAIが主張を抽出して検証します。
          </Typography>
        </Box>
      </Box>

      {/* エラー表示 */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      {/* 2カラム（左: 情報 / 右: アップロード） */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1.05fr' },
          gap: 2.5,
          alignItems: 'start',
        }}
      >
        {/* 動画アップロード（先に置かせる。名称がここから埋まる） */}
        <Box sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={stepBadgeSx}>1</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: tokens.ink }}>
              動画アップロード
            </Typography>
          </Box>

          <input
            type="file"
            accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
            onChange={handleFileInput}
            style={{ display: 'none' }}
            id="video-file-input"
            disabled={uploading}
          />

          {selectedFile ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.6,
                background: `linear-gradient(180deg, ${tokens.heroTo}, ${tokens.paper})`,
                border: '1.5px solid rgba(13,148,136,.35)',
                borderRadius: '14px',
                p: '14px 16px',
              }}
            >
              <Box
                component="img"
                src={IMG.create.video}
                alt=""
                sx={{ width: 44, height: 44, objectFit: 'contain', flexShrink: 0 }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: 13.5,
                    fontWeight: 800,
                    color: tokens.ink,
                    wordBreak: 'break-all',
                  }}
                >
                  {selectedFile.name}
                </Typography>
                <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: '2px' }}>
                  {formatFileSize(selectedFile.size)}
                </Typography>
              </Box>
              <IconButton
                aria-label="削除"
                disabled={uploading}
                onClick={() => {
                  setSelectedFile(null);
                  setError(null);
                  const input = document.getElementById(
                    'video-file-input',
                  ) as HTMLInputElement | null;
                  if (input) input.value = '';
                }}
                sx={{
                  ml: 'auto',
                  width: 30,
                  height: 30,
                  borderRadius: '9px',
                  border: `1px solid ${tokens.line}`,
                  bgcolor: tokens.paper,
                  color: tokens.faint,
                  flexShrink: 0,
                  '&:hover': { color: '#dc2626', borderColor: '#dc2626', bgcolor: tokens.paper },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ) : (
            <Box
              onClick={() => {
                if (!uploading) document.getElementById('video-file-input')?.click();
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? tokens.teal : tokens.faint,
                borderRadius: '12px',
                bgcolor: dragOver ? tokens.heroTo : 'transparent',
                p: '34px 20px',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                transition: '.15s',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                '&:hover': uploading ? {} : { borderColor: tokens.teal, bgcolor: tokens.heroTo },
              }}
            >
              <Box
                component="img"
                src={IMG.create.upload}
                alt=""
                sx={{
                  width: 76,
                  height: 76,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 6px 12px rgba(30,30,70,.14))',
                }}
              />
              <Typography sx={{ fontSize: 14, fontWeight: 900, color: tokens.ink }}>
                ここに動画を
                <Box component="span" sx={{ color: tokens.teal }}>
                  ドラッグ＆ドロップ
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, lineHeight: 1.5 }}>
                または クリックしてファイルを選択
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: 'rgba(100,116,139,0.14)',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 0.5,
                  fontSize: 11,
                  fontWeight: 800,
                  color: tokens.muted,
                }}
              >
                対応形式: MP4 / MOV / WebM（最大20GB）
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: 1.1,
              alignItems: 'flex-start',
              bgcolor: 'rgba(91,86,224,0.08)',
              border: '1px solid rgba(91,86,224,0.22)',
              borderRadius: '12px',
              p: '11px 13px',
              mt: 2,
              fontSize: 11.5,
              color: tokens.muted,
              lineHeight: 1.55,
            }}
          >
            <InfoIcon sx={{ fontSize: 16, flexShrink: 0, mt: '1px', color: tokens.indigo }} />
            <span>アップロード後、文字起こし→主張の抽出→検証へと自動で進みます。</span>
          </Box>
        </Box>

        {/* アイテム情報 */}
        <Box sx={panelSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={stepBadgeSx}>2</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 900, color: tokens.ink }}>
              アイテム情報
            </Typography>
          </Box>

          <Box sx={{ mb: 2.1 }}>
            <SourceMetaForm
              value={sourceMeta}
              onChange={(m) => {
                // 人が名称を打ち直したら、以後こちらで上書きしない
                if (m.title !== sourceMeta.title) setTitleAutoFilled(false);
                setSourceMeta(m);
              }}
              disabled={uploading}
              titleAutoFilled={titleAutoFilled}
            />
          </Box>

          {/* 言語は本番の実績が 54/54 日本語なので、既定のまま畳んでおく。
              いま何が選ばれているかは畳んだ状態でも見えるようにする。 */}
          <Box>
            <Box
              component="button"
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                width: '100%',
                px: 0,
                py: 0.75,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
                color: tokens.muted,
                fontSize: 12,
                fontWeight: 800,
                '&:hover': { color: tokens.ink },
              }}
            >
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  display: 'inline-block',
                  transition: 'transform .18s ease',
                  transform: advancedOpen ? 'rotate(90deg)' : 'none',
                }}
              >
                ›
              </Box>
              詳細設定
              <Box component="span" sx={{ fontWeight: 600, color: tokens.faint }}>
                （言語: {LANGUAGES.find((l) => l.value === language)?.label ?? language}）
              </Box>
            </Box>

            <Collapse in={advancedOpen} unmountOnExit={false}>
              <Box sx={{ pt: 1 }}>
                <FieldLabel required>言語</FieldLabel>
                <Select
                  fullWidth
                  size="small"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  sx={selectSx}
                >
                  {LANGUAGES.map((l) => (
                    <MenuItem key={l.value} value={l.value}>
                      {l.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography sx={hintSx}>音声の文字起こしに使用する言語です。</Typography>
              </Box>
            </Collapse>
          </Box>

          <Box>
            <FieldLabel required>テロップ</FieldLabel>
            <Select
              fullWidth
              size="small"
              value={videoMode}
              onChange={(e) => setVideoMode(e.target.value as VideoMode)}
              sx={selectSx}
            >
              <MenuItem value="with_telop">テロップ有り（通常）</MenuItem>
              <MenuItem value="offline">テロップ無し（未挿入の粗編）</MenuItem>
            </Select>
            <Typography sx={hintSx}>
              映像解析でテロップ抽出/誤り検出を行うか、未挿入として扱うかを指定します。
            </Typography>
          </Box>

          <Box>
            <FieldLabel>CM</FieldLabel>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={hasCommercials}
                  onChange={(e) => setHasCommercials(e.target.checked)}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: tokens.ink }}>CM・番宣を含む録画</Typography>
              }
              sx={{ ml: 0 }}
            />
            <Typography sx={hintSx}>
              指定すると文字起こしから CM
              区間を推定し、ファクト抽出・映像解析・規程チェックの対象から外します。
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* CTA */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, mt: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleStartUpload}
          disabled={!selectedFile || !isSourceMetaComplete(sourceMeta) || uploading}
          startIcon={<UploadIcon />}
          sx={{
            textTransform: 'none',
            background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
            color: '#fff',
            fontWeight: 800,
            fontSize: 14.5,
            borderRadius: '13px',
            px: 3,
            py: 1.5,
            boxShadow: '0 10px 24px rgba(40,50,90,.22)',
            '&:hover': {
              background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
              filter: 'brightness(1.05)',
              boxShadow: '0 10px 24px rgba(40,50,90,.22)',
            },
            '&.Mui-disabled': { background: '#cbd2e0', color: '#fff', boxShadow: 'none' },
          }}
        >
          アップロード開始
        </Button>
      </Box>

      {/* アップロード中オーバーレイ */}
      {uploading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(20,24,40,.45)',
            backdropFilter: 'blur(3px)',
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: '14px',
              p: '26px 34px',
              boxShadow: '0 24px 60px rgba(0,0,0,.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.8,
              maxWidth: 340,
              textAlign: 'center',
            }}
          >
            <CircularProgress sx={{ color: tokens.teal }} />
            <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#1b2030' }}>
              動画をアップロードしています…
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#5c6275' }}>
              処理画面で進捗を確認できます。
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CreateVideoItem;
